import React from 'react';
import { Button, Card, Flex, Typography, theme } from 'antd';
import { LeftOutlined } from '@ant-design/icons';

import { reportOnActivation } from '../ctaActivation';
import { useCfFlowEventTracker } from '../useCfFlowEventTracker';
import CloudflareStepHeader from './CloudflareStepHeader';

const { Paragraph, Text, Link } = Typography;

export const CF_ACCOUNT_EVENTS = {
  ctaClicked: 'Automated CF Integration: Account CTA Clicked',
} as const;

export type CfAccountCtaTarget = 'login' | 'signup';

const CLOUDFLARE_LOGIN_URL = 'https://dash.cloudflare.com/login';
const CLOUDFLARE_SIGNUP_URL = 'https://dash.cloudflare.com/sign-up';
const CLOUDFLARE_ADD_SITE_GUIDE_URL =
  'https://developers.cloudflare.com/learning-paths/clientless-access/initial-setup/add-site/';

// FIXME: no exact token — the design's 512px instructions and 420px checklist.
const INSTRUCTIONS_WIDTH = 512;
const CHECKLIST_WIDTH = 420;

export type CloudflareAccountStepProps = {
  domain: string;
  isConfirming?: boolean;
  onConfirm?: () => void;
  onGoBack?: () => void;
};

/** Cloudflare has no API for creating an account, so this step is always manual. */
const CloudflareAccountStep = ({
  domain,
  isConfirming = false,
  onConfirm,
  onGoBack,
}: CloudflareAccountStepProps) => {
  const { token } = theme.useToken();
  const track = useCfFlowEventTracker();
  const reportCta = (ctaTarget: CfAccountCtaTarget) =>
    reportOnActivation(() => track(CF_ACCOUNT_EVENTS.ctaClicked, { cta_target: ctaTarget }));

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Add domain to Cloudflare"
        description="To set up Prerender, it needs to be on Cloudflare."
      />

      <Card>
        <Flex vertical align="center" gap={token.marginMD}>
          <Paragraph
            type="secondary"
            style={{ marginBottom: 0, textAlign: 'center', maxWidth: INSTRUCTIONS_WIDTH }}
          >
            Go to cloudflare.com, log in to your account, and follow their setup
            wizard to add your domain. Once you&apos;re done, proceed to the next
            step.
          </Paragraph>

          <Button
            color="primary"
            variant="outlined"
            size="large"
            href={CLOUDFLARE_LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            {...reportCta('login')}
          >
            Log in to Cloudflare
          </Button>

          <Text>
            Don&apos;t have a Cloudflare account yet?{' '}
            <Link
              href={CLOUDFLARE_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline
              style={{ color: token.colorText }}
              {...reportCta('signup')}
            >
              Create account
            </Link>
          </Text>

          <Card
            size="small"
            style={{ background: token.colorFillQuaternary, width: '100%', maxWidth: CHECKLIST_WIDTH }}
          >
            <Text strong>Activities checklist</Text>
            <ol style={{ marginBottom: 0, paddingLeft: token.marginLG }}>
              <li>Log in to Cloudflare</li>
              <li>
                Add <Text strong>{domain}</Text> to Cloudflare.{' '}
                <Link
                  href={CLOUDFLARE_ADD_SITE_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline
                  style={{ color: token.colorText }}
                >
                  Learn more
                </Link>
              </li>
            </ol>
          </Card>
        </Flex>
      </Card>

      <Flex justify="space-between" align="center">
        {onGoBack ? (
          <Button type="link" icon={<LeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button type="primary" onClick={onConfirm} loading={isConfirming}>
          Continue
        </Button>
      </Flex>
    </Flex>
  );
};

export default CloudflareAccountStep;
